import { Flex, Box, useDisclosure, useToast, ToastId, Spinner, useBreakpointValue, Button, VStack, IconButton } from "@chakra-ui/react";
import { Cars } from "api/generated/graphql";
import CheckoutComponent from "components/CheckoutComponent/CheckoutComponent";
import { CryptoTabUpgrade } from "components/CheckoutModal/Crypto/CryptoTabUpgrade";
import FullscreenModal from "components/Modal/FullscreenModal";
import OrderTotal from "components/OrderTotal/OrderTotal";
import RToast from "components/Toast/Toast";
import UpgradeCar from "components/UpgradeCar/UpgradeCar";
import { useAnimatedTransition, useCheckout } from "context";
import { useGetAvailableTrailers, useGetCarUpgradeTrailerPrices, useGetGarage } from "hooks";
import useGetCarPrices from "hooks/Crypto/useGetCarPrices";
import useScheduleCarUpgrade from "hooks/TrialCar/useScheduleCarUpgrade";
import useScheduleCarUpgradeDiscount from "hooks/TrialCar/useScheduleCarUpgradeDiscount";
import { IconArrow, IconClose, IconUpgradeNew } from "icons";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import routes from "routes";


interface UpgradeCheckoutModalProps {
  carId: string;
  isOpen: boolean;
  onClose: () => void;
}

const projectId = process.env.NEXT_PUBLIC_CROSSMINT_API_ID || '';
const carsContractId =
  process.env.NEXT_PUBLIC_CARS_CONTRACT_ID || '';

const UpgradeCheckoutModal = ({ isOpen, onClose, carId }: UpgradeCheckoutModalProps) => {
  const router = useRouter();
  const { data: garageData, isLoading: isGarageLoading } = useGetGarage()
  const { data: carPrices } = useGetCarPrices();
  const { refetch: refetchTrailers } = useGetAvailableTrailers(
    undefined,
    'Local'
  );
  const { mutateAsync: scheduleCarUpgrade } = useScheduleCarUpgrade();
  const { mutateAsync: scheduleCarUpgradeDiscount } = useScheduleCarUpgradeDiscount();
  
  const car = garageData?.getGarage.find((car) => car.id === carId);


  const { showAnimatedTransition } = useAnimatedTransition();
  const toast = useToast();
  const toastIdRef = useRef<ToastId | null>(null);
  const { setIsLoadingPurchase, isLoadingPurchase } = useCheckout();
  const [mobileStep, setMobileStep] = useState(1);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleClose = () => {
    if (!isLoadingPurchase) {
      if (mobileStep === 2) {
        setMobileStep(1);
      } else {
        setMobileStep(1);
        onClose();
      }
    }
  };

  const onSuccessBuyDiscount = async (actionId: string) => {
    try {
      setIsLoadingPurchase(true);

      const res = await scheduleCarUpgradeDiscount({
        tokenId: actionId,
      });

      showAnimatedTransition({
        title: 'Car',
        id: 'trailer-purchased-success',
        subtitle: 'Upgraded',
        color: 'treasuredTeal.100',
        bgColor:
          'linear-gradient(116deg, #04360F -11.26%, #000400 30.41%, #011205 71.39%, #006845 104.43%)',
        gradientColor: {
          from: 'rgba(72, 223, 187, 0.2)',
          to: 'rgba(72, 223, 187, 0)',
        },
      });
      router.push(
        routes.marketplaceTrailers(
          res.scheduleCarUpgradeDiscount.referenceId || '',
          'UpgradeDiscount'
        )
      );
    } catch (error) {
      toastIdRef.current = toast({
        position: 'top',
        render: () => (
          <RToast
            variant="error"
            title="Upgrade failed"
            description="Please try again later, or contact support if the issue persists."
            onClose={() => toast.close(toastIdRef.current as ToastId)}
          />
        ),
      });
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isMobile && mobileStep === 2 ? 'Review and Pay' : isMobile && mobileStep === 1 ? 'Upgrade Summary' : 'Upgrade Car'}
      icon={
        <IconUpgradeNew 
          width={{ base: '14px', md: '28px' }}
          height={{ base: '16px', md: '32px' }}
        />
      }
      closeButtonProps={{
        onClick: handleClose,
        icon: mobileStep === 2 ? <IconArrow /> : <IconClose />,
      }}
    >
      {isGarageLoading ? (
        <Flex w="full" h="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : (
        <Flex 
          w="full" 
          gap={isMobile ? 0 : "1rem"}
          alignItems={isMobile ? "center" : "flex-start"} 
          flexDir={{ base: 'column', md: 'row' }}
          overflow={{ base: mobileStep === 1 ? "auto" : "visible", md: "auto" }}
          minH={{ base: "auto", md: "auto" }}
          pb={isMobile ? 0 : 0 }
          h="full"
          position="relative"
        >
          {!isMobile && (
            <>
              <Box w="50%" p="0">
                <UpgradeCar car={car as Cars} />
              </Box>
              <Box w="50%" p="0">
                <CheckoutComponent
                  collectionId={carsContractId}
                  projectId={projectId}
                  mintConfig={{
                    totalPrice: (
                      (carPrices?.getCarPrice?.price || 0)
                    ).toString(),
                    quantity: 1,
                  }}
                  CryptoTab={CryptoTabUpgrade}
                  cryptoTabProps={{
                    onSuccess: (actionId: string) => onSuccessBuyDiscount(actionId),
                  }}
                  onSuccessfulPurchase={(actionId: string) => onSuccessBuyDiscount(actionId)}
                  orderType="upgrade"
                />
              </Box>
            </>
          )}

          {isMobile && mobileStep === 1 && (
            <VStack w="full" spacing={4} flex={1} overflowY="auto" pb={20} p={4}>
              <UpgradeCar car={car as Cars} />
              <Flex
                position="absolute"
                bottom={0}
                left={0}
                w="full"
                flexDir="column"
                gap={2}
                background="linear-gradient(180deg, #1E2023 0%, #17181A 100%)"
                p={4}
                borderTop="1.5px solid rgba(255, 255, 255, 0.1)"
              >
                <OrderTotal 
                  total={carPrices?.getCarPrice?.price || 0}
                  type="upgrade"
                />
                <Flex
                  w="full"
                  background="linear-gradient(91.01deg, rgba(0, 255, 232, 0.1) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.1) 99.13%)"
                  borderRadius="4px"
                  p="2"
                  position="relative"
                  cursor="pointer"
                  _before={{
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "4px",
                    padding: "2px",
                    background: "linear-gradient(91.01deg, rgba(0, 255, 232, 0.2) 0.87%, rgba(0, 255, 232, 0) 30.35%, rgba(0, 255, 232, 0) 69.65%, rgba(0, 255, 232, 0.2) 99.13%)",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                  }}
                >
                  <Button 
                    variant="upgrade-button" 
                    w="full" 
                    onClick={() => setMobileStep(2)}
                    fontSize="12.5px"
                    lineHeight="16px"
                    fontWeight={400}
                    color="rgba(53, 255, 236, 0.8)"
                  >
                    Continue to Payment
                  </Button>
                </Flex>
              </Flex>
            </VStack>
          )}

          {isMobile && mobileStep === 2 && (
            <Box w="full" flex={1} overflowY="auto" px={4} pb={4}>
              <CheckoutComponent
                collectionId={carsContractId}
                projectId={projectId}
                mintConfig={{
                  totalPrice: (
                    (carPrices?.getCarPrice?.price || 0)
                  ).toString(),
                  quantity: 1,
                }}
                CryptoTab={CryptoTabUpgrade}
                cryptoTabProps={{
                  onSuccess: (actionId: string) => onSuccessBuyDiscount(actionId),
                }}
                onSuccessfulPurchase={(actionId: string) => onSuccessBuyDiscount(actionId)}
                orderType="upgrade"
              />
            </Box>
          )}
        </Flex>
      )}
    </FullscreenModal>
  );
};

export default UpgradeCheckoutModal;
